import type React from "react";

import { m } from "@/paraglide/messages.js";

interface SignalMatchingProps {
  className?: string;
}

/**
 * Bento illustration: signal matching. A stack of live market signals, each
 * already matched to the clients who should hear it first. Ported from the
 * reference design's card stack.
 */
export const SignalMatching: React.FC<SignalMatchingProps> = ({ className = "" }) => {
  const signals = [
    {
      icon: <TrendUpIcon />,
      title: m.landing_signal_energy_title(),
      meta: m.landing_signal_energy_meta(),
    },
    {
      icon: <CurveIcon />,
      title: m.landing_signal_rates_title(),
      meta: m.landing_signal_rates_meta(),
    },
    {
      icon: <SparklesIcon />,
      title: m.landing_signal_tech_title(),
      meta: m.landing_signal_tech_meta(),
    },
  ];

  return (
    <div
      className={`relative mx-auto h-full w-full shrink-0 rounded-[10.047px] bg-gradient-to-b from-[color-mix(in_oklch,var(--card)_40%,transparent)] to-transparent backdrop-blur-[8.372px] ${className}`}
      role="img"
      aria-label={m.landing_capability_desk_illustration_alt()}
    >
      <div className="mx-6 mt-6 flex h-full w-[calc(100%-48px)] flex-col items-center justify-start gap-4 overflow-hidden rounded-t-[9.628px] border-[0.802px] border-border bg-gradient-to-b from-[color-mix(in_oklch,var(--primary)_5%,transparent)] to-transparent p-5 backdrop-blur-[16px]">
        {signals.map((signal, index) => (
          <div
            key={signal.title}
            className="relative flex w-full max-w-[320px] shrink-0 flex-row items-start gap-[8.658px] overflow-hidden rounded-[8.658px] border-[0.541px] border-border bg-gradient-to-b from-[color-mix(in_oklch,var(--card)_20%,transparent)] to-transparent px-[8.658px] py-[6.494px] shadow-[0px_1.082px_2.165px_0px_rgba(0,0,0,0.12)] backdrop-blur-[19.481px]"
          >
            <div className="flex shrink-0 flex-row items-center justify-start gap-[8.658px] pt-[3.247px]">
              <div className="flex size-[17.316px] shrink-0 items-center justify-center overflow-hidden">
                {signal.icon}
              </div>
            </div>
            <div
              className={`flex shrink-0 flex-col items-start justify-center gap-[2.164px] ${
                index === 1 ? "min-h-px min-w-px flex-1 basis-0" : ""
              }`}
            >
              <div className="shrink-0 whitespace-pre font-sans text-[10.823px] font-normal leading-[17.316px] text-foreground">
                {signal.title}
              </div>
              <div
                className={`shrink-0 font-sans text-[10.823px] font-normal leading-[17.316px] text-muted-foreground ${
                  index === 1 ? "w-full min-w-full overflow-hidden text-ellipsis whitespace-nowrap" : "whitespace-pre"
                }`}
              >
                {signal.meta}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Glyph = ({ d }: { d: string }) => (
  <svg className="size-[13.885px]" viewBox="0 0 14 14" fill="none">
    <path
      d={d}
      stroke="var(--foreground)"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      opacity="0.8"
    />
  </svg>
);

const TrendUpIcon = () => <Glyph d="M1.75 10.5L5.25 6.125L8.166 8.458L12.25 3.5M12.25 3.5H8.75M12.25 3.5V7" />;

const CurveIcon = () => <Glyph d="M1.75 10.5C5.25 10.5 6.417 8.167 7.583 5.833C8.75 3.5 10.208 3.5 12.25 3.5" />;

const SparklesIcon = () => (
  <Glyph d="M7 1.75L8.225 5.775L12.25 7L8.225 8.225L7 12.25L5.775 8.225L1.75 7L5.775 5.775L7 1.75Z" />
);
